import { useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  IconButton,
  Chip,
  TablePagination,
  Box,
} from "@mui/material";
import { Visibility as EyeIcon, Edit as EditIcon, Phone as PhoneIcon } from "@mui/icons-material";
import moment from "moment";

const FOLLOW_UP_HEADERS = [
  "LEAD NAME",
  "PHONE",
  "EMAIL",
  "FOLLOW UP DATE",
  "STAGE",
  "INTEREST",
  "DISPOSITION",
  "REMARKS",
  "CREATED AT",
  "ACTIONS"
];

export const AppointmentsTable = ({ leads = [], onViewLead }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedLeads = leads.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <PhoneIcon color="primary" />
        <Typography variant="h6" fontWeight={700} color="text.primary">
          Follow Ups
        </Typography>
      </Box>

      <Paper sx={{ boxShadow: "none", border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden" }}>
        <TableContainer sx={{
          overflow: "auto",
          maxWidth: '100%',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}>
          <Table sx={{ minWidth: 1400 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                {FOLLOW_UP_HEADERS.map((header) => (
                  <TableCell key={header} sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: "#475569", fontSize: "0.7rem", letterSpacing: "0.03em" }}>
                      {header}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedLeads.map((lead) => {
                return (
                  <TableRow key={lead.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {lead.dealName}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{lead.mobile}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{lead.email || "—"}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color="primary">
                        {lead.followUpDate ? moment(lead.followUpDate).format("MMM DD, YYYY hh:mm A") : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={lead.stageName || "New"}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: "0.65rem", borderRadius: "6px" }}
                      />
                    </TableCell>

                    <TableCell>
                        <Chip
                          label={lead.interest || "—"}
                          size="small"
                          sx={{
                            bgcolor: lead.interest === "Interested" ? "#DCFCE7" : "#F3F4F6",
                            color: lead.interest === "Interested" ? "#166534" : "#6B7280",
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            borderRadius: "6px"
                          }}
                        />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {lead.disposition || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={lead.description}>
                        {lead.description || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="text.disabled">
                        {moment(lead.createdAt).format("MMM DD, YYYY")}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => onViewLead(lead)}
                        sx={{ color: "#6366f1" }}
                      >
                        <EditIcon size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}

              {paginatedLeads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={FOLLOW_UP_HEADERS.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No follow ups found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={leads.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: "1px solid #f1f5f9" }}
        />
      </Paper>
    </Box>
  );
};
